/**
 * Seed data for feature discovery nudges.
 * Run once via SuperAdmin or browser console to populate Firestore.
 *
 * Usage from NudgeManager: import { SEED_NUDGES } from '@/store/nudges-seed';
 *
 * Field conventions:
 *   - `text`:        runtime body string — FeatureNudge.vue reads this.
 *   - `description`: editor body string  — NudgeManager.vue reads/writes this.
 *
 * Both fields must carry the SAME copy. The editor's save path mirrors
 * description → text on every save, so edits propagate to the runtime.
 * The seed file just needs to start both fields in sync.
 */

export const SEED_NUDGES = [
  // === TASKS PAGE (/app/todo) ===
  {
    title: 'Organize tasks visually with Kanban',
    description: 'Drag and drop tasks across custom columns — perfect for tracking patient follow-ups, treatment stages, and team workflows at a glance.',
    text: 'Drag and drop tasks across custom columns — perfect for tracking patient follow-ups, treatment stages, and team workflows at a glance.',
    icon: 'fa-th-large',
    ctaText: 'Try Kanban Board',
    ctaRoute: '/app/kanban',
    showOnRoutes: ['/app/todo'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },
  {
    title: 'Automate task creation with Workflows',
    description: 'Automatically create tasks when appointments are broken, recalls are overdue, or forms are submitted. No more manual follow-up tracking.',
    text: 'Automatically create tasks when appointments are broken, recalls are overdue, or forms are submitted. No more manual follow-up tracking.',
    icon: 'fa-magic',
    ctaText: 'Set Up a Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/app/todo'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 2,
  },

  // === KANBAN PAGE (/app/kanban) ===
  {
    title: 'Auto-populate Kanban cards with Workflows',
    description: 'Set up a workflow that automatically creates Kanban cards when specific events happen — like a new patient scheduling or a treatment plan being accepted.',
    text: 'Set up a workflow that automatically creates Kanban cards when specific events happen — like a new patient scheduling or a treatment plan being accepted.',
    icon: 'fa-magic',
    ctaText: 'Create a Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/app/kanban'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === ADMIN DASHBOARD (/app/admin) ===
  {
    title: 'Start your day with Huddle',
    description: 'See production goals, new patients, hygiene stats, and team KPIs — all in one morning dashboard. Perfect for your daily team standup.',
    text: 'See production goals, new patients, hygiene stats, and team KPIs — all in one morning dashboard. Perfect for your daily team standup.',
    icon: 'fa-users',
    ctaText: 'Open Huddle',
    ctaRoute: '/app/huddle',
    showOnRoutes: ['/app/admin'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },
  {
    title: 'Track team performance with Team Insights',
    description: 'See who\'s completing follow-ups, how tasks are distributed, and identify bottlenecks. Data-driven visibility into your team\'s productivity.',
    text: 'See who\'s completing follow-ups, how tasks are distributed, and identify bottlenecks. Data-driven visibility into your team\'s productivity.',
    icon: 'fa-bar-chart',
    ctaText: 'View Team Insights',
    ctaRoute: '/app/team-insights',
    showOnRoutes: ['/app/admin'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 2,
  },

  // === SMS CONVERSATIONS (/app/conversations) ===
  {
    title: 'Save patient-sent photos to Open Dental',
    description: 'When patients text in photos (xrays, insurance cards, etc.), Teamio can now automatically save them to a folder of your choice in Open Dental\'s imaging tab. Configure which OD category to use in SMS settings.',
    text: 'When patients text in photos (xrays, insurance cards, etc.), Teamio can now automatically save them to a folder of your choice in Open Dental\'s imaging tab. Configure which OD category to use in SMS settings.',
    icon: 'fa-camera',
    ctaText: 'Open SMS Settings',
    ctaRoute: '/app/conversations?openSettings=photo-imaging',
    showOnRoutes: ['/app/conversations'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 0,
  },
  {
    title: 'Reach all your patients at once with Campaigns',
    description: 'Send targeted SMS or email campaigns to patients based on procedure codes, insurance, appointment status, and more. Great for recall reminders and promotions.',
    text: 'Send targeted SMS or email campaigns to patients based on procedure codes, insurance, appointment status, and more. Great for recall reminders and promotions.',
    icon: 'fa-bullhorn',
    ctaText: 'Create a Campaign',
    ctaRoute: '/app/campaigns',
    showOnRoutes: ['/app/conversations'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },
  {
    title: 'Save time with message templates',
    description: 'Create reusable templates in your Wiki for common responses — appointment confirmations, insurance questions, post-op instructions. Insert them with one click.',
    text: 'Create reusable templates in your Wiki for common responses — appointment confirmations, insurance questions, post-op instructions. Insert them with one click.',
    icon: 'fa-file-text-o',
    ctaText: 'Manage Wiki Templates',
    ctaRoute: '/app/wiki',
    showOnRoutes: ['/app/conversations'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 2,
  },

  // === CAMPAIGNS PAGE (/app/campaigns) ===
  {
    title: 'Build email templates in your Wiki',
    description: 'Create beautiful email templates with your Wiki editor — add images, formatting, and patient variables. Then use them directly in campaigns.',
    text: 'Create beautiful email templates with your Wiki editor — add images, formatting, and patient variables. Then use them directly in campaigns.',
    icon: 'fa-book',
    ctaText: 'Open Wiki',
    ctaRoute: '/app/wiki',
    showOnRoutes: ['/app/campaigns'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === OVERDUE RECALL ===
  {
    title: 'Automate recall follow-ups with Workflows',
    description: 'Set up automated SMS and email sequences for overdue recall patients. Stop manually calling — let workflows handle the first few touches.',
    text: 'Set up automated SMS and email sequences for overdue recall patients. Stop manually calling — let workflows handle the first few touches.',
    icon: 'fa-magic',
    ctaText: 'Create Recall Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/overdue-recall'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },
  {
    title: 'Send a recall campaign to all overdue patients',
    description: 'Use Campaigns to reach every overdue patient at once with a personalized recall message. Filter by months overdue, provider, or insurance plan.',
    text: 'Use Campaigns to reach every overdue patient at once with a personalized recall message. Filter by months overdue, provider, or insurance plan.',
    icon: 'fa-bullhorn',
    ctaText: 'Create Recall Campaign',
    ctaRoute: '/app/campaigns',
    showOnRoutes: ['/overdue-recall'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 2,
  },

  // === FORMS PAGE (/app/forms) ===
  {
    title: 'Send forms automatically with Workflows',
    description: 'Auto-send intake forms to new patients, post-op instructions after procedures, or consent forms before appointments. Zero manual effort.',
    text: 'Auto-send intake forms to new patients, post-op instructions after procedures, or consent forms before appointments. Zero manual effort.',
    icon: 'fa-magic',
    ctaText: 'Set Up a Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/app/forms'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === WIKI PAGE (/app/wiki) ===
  {
    title: 'Use Wiki pages as SMS & email templates',
    description: 'Your Wiki pages can double as message templates. Create a template once, then use it in Campaigns, Workflows, and SMS conversations with patient variables.',
    text: 'Your Wiki pages can double as message templates. Create a template once, then use it in Campaigns, Workflows, and SMS conversations with patient variables.',
    icon: 'fa-bullhorn',
    ctaText: 'Try Campaigns',
    ctaRoute: '/app/campaigns',
    showOnRoutes: ['/app/wiki'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === HUDDLE PAGE (/app/huddle) ===
  {
    title: 'Track follow-ups with Team Insights reports',
    description: 'See which team members are completing their follow-ups and who needs support. Custom reports break down performance by task type and time period.',
    text: 'See which team members are completing their follow-ups and who needs support. Custom reports break down performance by task type and time period.',
    icon: 'fa-bar-chart',
    ctaText: 'View Team Insights',
    ctaRoute: '/app/team-insights',
    showOnRoutes: ['/app/huddle'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === CHAT PAGE (/app/chat) ===
  {
    title: 'Share files and images with your team',
    description: 'Drag and drop images, documents, and videos directly into chat. Supports up to 10MB images, 25MB docs, and 50MB video/audio.',
    text: 'Drag and drop images, documents, and videos directly into chat. Supports up to 10MB images, 25MB docs, and 50MB video/audio.',
    icon: 'fa-paperclip',
    ctaText: 'Try It Now',
    ctaRoute: '/app/chat',
    showOnRoutes: ['/app/chat'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === BROKEN APPOINTMENTS ===
  {
    title: 'Automate broken appointment follow-ups',
    description: 'Set up a workflow that automatically texts patients when they miss an appointment — with a link to reschedule. No more chasing no-shows manually.',
    text: 'Set up a workflow that automatically texts patients when they miss an appointment — with a link to reschedule. No more chasing no-shows manually.',
    icon: 'fa-magic',
    ctaText: 'Create a Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/broken-appointments'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === UNSCHEDULED TREATMENT ===
  {
    title: 'Re-engage patients with a treatment campaign',
    description: 'Send a targeted campaign to patients with unscheduled treatment plans. Filter by procedure type, provider, or insurance to personalize the message.',
    text: 'Send a targeted campaign to patients with unscheduled treatment plans. Filter by procedure type, provider, or insurance to personalize the message.',
    icon: 'fa-bullhorn',
    ctaText: 'Create a Campaign',
    ctaRoute: '/app/campaigns',
    showOnRoutes: ['/unscheduled-treatment'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === WORKFLOW LIST (/app/workflow2-list) ===
  {
    title: 'Workflows can send forms, SMS, email, and more',
    description: 'Each workflow can trigger multiple actions — send a form, text the patient, email the provider, create a task, or add a Kanban card. All from one automation.',
    text: 'Each workflow can trigger multiple actions — send a form, text the patient, email the provider, create a task, or add a Kanban card. All from one automation.',
    icon: 'fa-sitemap',
    ctaText: 'Create a Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/app/workflow2-list'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === PRACTICE PULSE ===
  {
    title: 'Automate patient flow with Workflows',
    description: 'Set up workflows that trigger when patients check in, complete appointments, or get scheduled. Automatically notify staff, send forms, or create follow-up tasks.',
    text: 'Set up workflows that trigger when patients check in, complete appointments, or get scheduled. Automatically notify staff, send forms, or create follow-up tasks.',
    icon: 'fa-magic',
    ctaText: 'Set Up a Workflow',
    ctaRoute: '/app/workflow2-list',
    showOnRoutes: ['/app/practice-pulse'],
    targetRoles: [],
    targetSiteIDs: [],
    active: true,
    priority: 1,
  },

  // === COMING SOON ===
  {
    title: 'AI chatbot powered by your Wiki',
    description: 'Soon your website chatbot will use your Wiki knowledge base to answer patient questions 24/7 — directions, insurance accepted, appointment types, and more.',
    text: 'Soon your website chatbot will use your Wiki knowledge base to answer patient questions 24/7 — directions, insurance accepted, appointment types, and more.',
    icon: 'fa-robot',
    ctaText: 'Stay Tuned',
    ctaRoute: '',
    type: 'coming_soon',
    showOnRoutes: ['/app/wiki', '/app/practice-pulse'],
    // AI features are not released yet — keep these Super-Admin-only so
    // the general staff doesn't see "Coming Soon" teasers for things we
    // can't deliver on. Flip to [] when the feature ships.
    targetRoles: ['Super Admin'],
    targetSiteIDs: [],
    active: true,
    priority: 99,
  },
  {
    title: 'AI voice receptionist',
    description: 'An AI-powered phone agent that answers calls, schedules appointments, and handles common patient questions — all integrated with your practice management system.',
    text: 'An AI-powered phone agent that answers calls, schedules appointments, and handles common patient questions — all integrated with your practice management system.',
    icon: 'fa-phone',
    ctaText: 'Stay Tuned',
    ctaRoute: '',
    type: 'coming_soon',
    showOnRoutes: ['/app/admin', '/app/practice-pulse'],
    // AI features are not released yet — keep these Super-Admin-only so
    // the general staff doesn't see "Coming Soon" teasers for things we
    // can't deliver on. Flip to [] when the feature ships.
    targetRoles: ['Super Admin'],
    targetSiteIDs: [],
    active: true,
    priority: 99,
  },
];
